import React, { useState, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';
import { API } from '../utils/apk';

const DeliveryBoyMap = () => {
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [selectedBoy, setSelectedBoy] = useState(null);
  const [places, setPlaces] = useState({});
  const [center, setCenter] = useState({ lat: 22.302, lng: 88.128 }); // Default center
  const [zoom, setZoom] = useState(15); // Default zoom level
  const [map, setMap] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [prevLocations, setPrevLocations] = useState({}); // Track previous locations

  // Fetch place name using reverse geocoding
  const fetchPlaceName = async (id, lat, lng) => {
    try {
      const geocodingUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
      const response = await axios.get(geocodingUrl);
      const placeName = response?.data?.display_name || 'Unknown Location';

      setPlaces((prevPlaces) => ({ ...prevPlaces, [id]: placeName }));
    } catch (error) {
      console.error(`Error fetching place name for ID ${id}:`, error);
    }
  };

  // Fetch delivery boys data
  const fetchDeliveryBoys = async () => {
    try {
      const response = await axios.get(`${API}/get/all/delivery/boy`);
      const boys = response?.data.data;
      setDeliveryBoys(boys);

      // Fetch place names for initial locations
      await Promise.all(
        boys.map((boy) =>
          fetchPlaceName(boy._id, boy.latitude, boy.longitude)
        )
      );

      // Save initial locations
      const initialLocations = {};
      boys.forEach((boy) => {
        initialLocations[boy._id] = { lat: boy.latitude, lng: boy.longitude };
      });
      setPrevLocations(initialLocations);
    } catch (error) {
      console.error('Error fetching delivery boys:', error);
    }
  };

  // Fetch updated location of delivery boys every 2 seconds
  const fetchUpdatedLocations = async () => {
    try {
      const response = await axios.get(`${API}/get/all/delivery/boy`);
      const updatedBoys = response?.data.data;
      setDeliveryBoys(updatedBoys);

      // Check if any delivery boy moved and apply 3D effect
      updatedBoys.forEach((boy) => {
        const prevLocation = prevLocations[boy._id];
        if (
          !prevLocation ||
          prevLocation.lat !== boy.latitude ||
          prevLocation.lng !== boy.longitude
        ) {
          setPrevLocations((prev) => ({
            ...prev,
            [boy._id]: { lat: boy.latitude, lng: boy.longitude },
          }));
        }
      });

      // Fetch place names for updated locations
      await Promise.all(
        updatedBoys.map((boy) =>
          fetchPlaceName(boy._id, boy.latitude, boy.longitude)
        )
      );
    } catch (error) {
      console.error('Error fetching updated locations:', error);
    }
  };

  useEffect(() => {
    fetchDeliveryBoys(); // Fetch initial data

    // Fetch updated locations every 2 seconds
    const intervalId = setInterval(fetchUpdatedLocations, 2000);
    return () => clearInterval(intervalId); // Cleanup on unmount
  }, []);

  // Handle delivery boy click to show InfoWindow and reset tilt
  const handleDeliveryBoyClick = (boy) => {
    setSelectedBoy(boy);
    map.panTo({ lat: boy.latitude, lng: boy.longitude });

    map.setOptions({
      zoom: 19,
    });
  };

  const mapOptions = {
    streetViewControl: false,
    zoomControl: true,
    scrollwheel: true,
    fullscreenControl: true,
    mapTypeControl: false,
    maxZoom: 25,
  };

  const handleLoad = (mapInstance) => {
    setMap(mapInstance);
  };

  const filteredDeliveryBoys = deliveryBoys
    .filter((boy) =>
      boy.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="flex flex-row h-screen mt-4 bg-gray-100">
      {/* Sidebar for listing delivery boys */}
      <div className="w-1/6 p-4 bg-white h-4/5 shadow-lg overflow-hidden">
        <h2 className="text-xl font-bold mb-4">Delivery Boys</h2>

        {/* Search Input to Filter Delivery Boys */}
        <input
          type="text"
          className="w-full p-2 mb-4 border rounded"
          placeholder="Search delivery boy..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <ul className="overflow-y-auto h-96">
          {filteredDeliveryBoys.map((boy) => (
            <li
              key={boy._id}
              className={`p-2 cursor-pointer rounded ${
                selectedBoy && selectedBoy._id === boy._id
                  ? 'bg-blue-500 text-white'
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => handleDeliveryBoyClick(boy)}
            >
              {boy.name}
            </li>
          ))}
        </ul>
      </div>

      {/* Map container */}
      <div className="flex-grow h-4/5 overflow-hidden">
        <LoadScript googleMapsApiKey="AIzaSyCuzLFsuMKfJSLZckw62Ww965pY7cfVcW8">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={zoom}
            options={mapOptions}
            onLoad={handleLoad}
          >
            {/* Plot delivery boys on the map */}
            {deliveryBoys.map((boy) => (
              <Marker
                key={boy._id}
                position={{ lat: boy.latitude, lng: boy.longitude }}
              />
            ))}

            {/* Show InfoWindow only when name is clicked */}
            {selectedBoy && (
              <InfoWindow
              position={{
                lat: selectedBoy.latitude + 0.00005, // Slightly move up
                lng: selectedBoy.longitude,
              }}
              onCloseClick={() => setSelectedBoy(null)}
            >
              <div
                className="p-2"
                style={{ width: 'fit-content', height: 'fit-content' }}
              >
                <h3 className="text-lg font-semibold">{selectedBoy.name}</h3>
                <p>Mobile: {selectedBoy.mobileNo}</p>
                <p>Location: {places[selectedBoy._id]}</p>
              </div>
            </InfoWindow>
            
            )}
          </GoogleMap>
        </LoadScript>
      </div>
    </div>
  );
};

export default DeliveryBoyMap;